import React, { useEffect } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  flip,
  FloatingPortal,
  FloatingFocusManager,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';

/* ContextMenu — anchored at (x, y), auto-flips/shifts near viewport edges
   via Floating UI (no more manual clamp math). Escape and outside click
   close via `useDismiss`. Focus is trapped within the menu for keyboard
   users (Tab stays inside until Escape).
   Props:
     open      — bool
     x, y      — viewport coords (from event.clientX/Y)
     items     — [{ id, label, hint?, disabled?, danger?, onSelect }]
     onClose   — fired on outside click, Esc, or after item onSelect */
export default function ContextMenu({ open, x, y, items = [], onClose }) {
  /* Virtual reference: a 1x1 rect at the click coordinates — lets Floating
     UI position the menu as if it were anchored to an invisible element
     at the pointer. */
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (o) => { if (!o) onClose(); },
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!open) return;
    refs.setPositionReference({
      getBoundingClientRect: () => ({
        width: 0, height: 0, x, y, top: y, left: x, right: x, bottom: y,
      }),
    });
  }, [open, x, y, refs]);

  const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true });
  const role = useRole(context, { role: 'menu' });
  const { getFloatingProps, getItemProps } = useInteractions([dismiss, role]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="min-w-[220px] max-w-[280px] bg-surface-el border border-border-strong rounded-md shadow-lg py-[0.3rem] z-[110] flex flex-col outline-none"
          onContextMenu={(e) => e.preventDefault()}
        >
          {items.map((it) => {
            const base = 'flex flex-col items-start bg-transparent border-0 px-[0.85rem] py-[0.4rem] cursor-pointer font-[inherit] text-[0.82rem] leading-[1.2] text-left enabled:hover:bg-surface-hover disabled:cursor-not-allowed transition-colors';
            const color = it.disabled
              ? 'text-text-dim'
              : it.danger
                ? 'text-accent-red enabled:hover:text-accent-red'
                : 'text-text enabled:hover:text-accent-gold';
            return (
              <button
                key={it.id}
                type="button"
                className={`${base} ${color}`}
                disabled={it.disabled}
                role="menuitem"
                {...getItemProps({
                  onClick: () => { if (!it.disabled) { it.onSelect?.(); onClose(); } },
                })}
              >
                <span>{it.label}</span>
                {it.hint && <em className="text-[0.68rem] text-text-muted not-italic mt-[0.1rem]">{it.hint}</em>}
              </button>
            );
          })}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
